import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface Announcement {
  id: string;
  title: string;
  content?: string;
  type: string;
  created_at?: string;
}

const typeConfig: Record<string, { icon: string; bg: string; text: string; dot: string; badge: string; badgeText: string }> = {
  promo: { icon: 'ri-gift-line', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', badge: 'bg-amber-100', badgeText: 'text-amber-600' },
  warning: { icon: 'ri-alarm-warning-line', bg: 'bg-rose-50', text: 'text-rose-600', dot: 'bg-rose-400', badge: 'bg-rose-100', badgeText: 'text-rose-600' },
  info: { icon: 'ri-megaphone-line', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400', badge: 'bg-emerald-100', badgeText: 'text-emerald-700' },
};

const typeLabel: Record<string, string> = {
  promo: '优惠',
  warning: '通知',
  info: '公告',
};

// ─── 全部公告弹窗 ─────────────────────────────────────────────────────────────
const AnnouncementModal = ({
  announcements,
  onClose,
}: {
  announcements: Announcement[];
  onClose: () => void;
}) => (
  <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={onClose}>
    <div className="absolute inset-0 bg-black/40" />
    <div
      className="relative w-full max-w-[480px] bg-white rounded-t-3xl overflow-hidden"
      style={{ animation: 'slideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Handle */}
      <div className="flex justify-center pt-3 pb-1">
        <div className="w-10 h-1 bg-gray-200 rounded-full" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <i className="ri-notification-3-line text-gray-700 text-base" />
          <h2 className="font-bold text-gray-900 text-base">全部公告</h2>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
            {announcements.length} 条
          </span>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
        >
          <i className="ri-close-line text-xl" />
        </button>
      </div>

      {/* List */}
      <div className="overflow-y-auto max-h-[65vh] px-4 py-3 space-y-3 pb-8">
        {announcements.map((ann, idx) => {
          const cfg = typeConfig[ann.type] ?? typeConfig.info;
          return (
            <div
              key={ann.id}
              className={`rounded-2xl p-4 ${cfg.bg}`}
              style={{ animation: `fadeSlideIn 0.25s ease ${idx * 0.05}s both` }}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 ${cfg.badge}`}>
                  <i className={`${cfg.icon} text-base ${cfg.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badge} ${cfg.badgeText}`}>
                      {typeLabel[ann.type] ?? '公告'}
                    </span>
                    {ann.created_at && (
                      <span className="text-xs text-gray-400">
                        {new Date(ann.created_at).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm font-semibold leading-snug ${cfg.text}`}>{ann.title}</p>
                  {ann.content && (
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{ann.content}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    <style>{`
      @keyframes slideUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
      }
      @keyframes fadeSlideIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  </div>
);

// ─── 公告栏主组件 ─────────────────────────────────────────────────────────────
const AnnouncementBar = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [current, setCurrent] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAnnouncements = async () => {
    const { data } = await supabase
      .from('announcements')
      .select('id, title, content, type, created_at')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    setAnnouncements((data as Announcement[]) ?? []);
  };

  useEffect(() => {
    fetchAnnouncements();

    const channel = supabase
      .channel('announcements-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'announcements' },
        () => { fetchAnnouncements(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (announcements.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent((p) => (p + 1) % announcements.length);
    }, 3000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [announcements.length]);

  if (announcements.length === 0) return null;

  const ann = announcements[current];
  const cfg = typeConfig[ann.type] ?? typeConfig.info;

  return (
    <>
      <button
        className={`w-full mx-0 mt-3 px-4 cursor-pointer`}
        onClick={() => setShowModal(true)}
      >
        <div className={`rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 ${cfg.bg} transition-all duration-300 active:opacity-80`}>
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className={`${cfg.icon} text-base ${cfg.text}`} />
          </div>
          <div className="flex-1 overflow-hidden text-left">
            <p
              key={ann.id}
              className={`text-xs font-medium truncate ${cfg.text}`}
              style={{ animation: 'slideUpFade 0.35s ease' }}
            >
              {ann.title}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {announcements.length > 1 && (
              <div className="flex items-center gap-1">
                {announcements.map((_, i) => (
                  <span
                    key={i}
                    className={`rounded-full transition-all duration-300 ${i === current ? `w-3 h-1.5 ${cfg.dot}` : `w-1.5 h-1.5 bg-gray-300`}`}
                  />
                ))}
              </div>
            )}
            <i className={`ri-arrow-right-s-line text-sm ${cfg.text} opacity-60`} />
          </div>
        </div>
      </button>

      {showModal && (
        <AnnouncementModal
          announcements={announcements}
          onClose={() => setShowModal(false)}
        />
      )}

      <style>{`
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default AnnouncementBar;
